import React from 'react';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const StatusAndComplianceStep = ({ formData, errors, handleInputChange, handleCheckboxChange }) => {
  const statusOptions = [
    { value: 'planning', label: 'Planning', description: 'Site in planning phase' },
    { value: 'active', label: 'Active', description: 'Construction work in progress' },
    { value: 'on-hold', label: 'On Hold', description: 'Temporarily paused' },
    { value: 'completed', label: 'Completed', description: 'Project finished' }
  ];

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6">
        <div className="flex gap-3 md:gap-4">
          <div className="flex-shrink-0">
            <Icon name="Settings" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base lg:text-lg">
              Site Status & Compliance Setup
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Set the current status and understand the compliance requirements for your site.
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-6 md:space-y-8">
        <Select
          label="Current Site Status"
          options={statusOptions}
          value={formData?.status}
          onChange={(value) => handleInputChange({ target: { name: 'status', value } })}
          error={errors?.status}
          required
          description="Select the current phase of this construction project"
        />

        <div className="bg-muted/50 border border-border rounded-lg p-4 md:p-6">
          <h4 className="font-semibold text-foreground mb-4 text-sm md:text-base lg:text-lg flex items-center gap-2">
            <Icon name="FileText" size={20} />
            Understanding Compliance Requirements
          </h4>
          
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-1">
                <div className="w-2 h-2 bg-success rounded-full"></div>
              </div>
              <div>
                <p className="font-medium text-foreground text-sm md:text-base">
                  Green Status - Fully Compliant
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  All required documents uploaded and valid, all workers inducted
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-1">
                <div className="w-2 h-2 bg-warning rounded-full"></div>
              </div>
              <div>
                <p className="font-medium text-foreground text-sm md:text-base">
                  Amber Status - Action Required
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Documents expiring within 30 days or minor items missing
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-1">
                <div className="w-2 h-2 bg-error rounded-full"></div>
              </div>
              <div>
                <p className="font-medium text-foreground text-sm md:text-base">
                  Red Status - Non-Compliant
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Critical documents missing or expired, immediate action needed
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-muted/50 border border-border rounded-lg p-4 md:p-6">
          <h4 className="font-semibold text-foreground mb-4 text-sm md:text-base lg:text-lg flex items-center gap-2">
            <Icon name="AlertCircle" size={20} />
            Required Document Categories
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle2" size={18} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground text-sm md:text-base">H&S Documents (policies, risk assessments, method statements, COSHH, etc.)</p>
                <p className="text-sm text-muted-foreground">Required for all construction activities</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Icon name="CheckCircle2" size={18} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground text-sm md:text-base">Public Liability Insurance</p>
                <p className="text-sm text-muted-foreground">Minimum £5 million cover required</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Icon name="CheckCircle2" size={18} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground text-sm md:text-base">Worker Training Certificates</p>
                <p className="text-sm text-muted-foreground">CSCS cards, SMSTS, SSSTS as applicable</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Icon name="CheckCircle2" size={18} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground text-sm md:text-base">Plant & Equipment Certificates</p>
                <p className="text-sm text-muted-foreground">LOLER, PUWER inspections and certifications</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <Checkbox
            label="I understand the compliance requirements"
            description="I acknowledge that I am responsible for uploading and maintaining all required documentation for this site"
            checked={formData?.acknowledgeCompliance}
            onChange={handleCheckboxChange}
            name="acknowledgeCompliance"
            error={errors?.acknowledgeCompliance}
            required
          />
        </div>
      </div>
    </div>
  );
};

export default StatusAndComplianceStep;